import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { createPortalSession } from '../../pages/player/api';
import { useLexicon } from '../../hooks/useLexicon';

// Substitute {placeholders} in a lexicon template with React nodes, in order,
// preserving per-language word order for bold/dynamic bits.
const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

// Billing section rendered inside the Player Profile page (PRD §11.8).
//
// Track A (self_serve): current plan + "Manage billing" → opens the Stripe
// Customer Portal in a new tab. Cancel / update card / view invoices /
// switch plan all live there.
//
// Track B (team_admin): passive view — billing is offline, the player
// contacts their team admin to renew/cancel.

const formatDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

const planLabel = (subscription) => {
  if (!subscription) return null;
  if (subscription.stripe_price_lookup_key?.includes('annual')) return 'Annual €690';
  if (subscription.stripe_price_lookup_key?.includes('monthly')) return 'Monthly €69';
  if (subscription.plan === 'annual') return 'Annual';
  if (subscription.plan === 'monthly') return 'Monthly';
  return null;
};

const PlayerBillingSection = ({ subscription }) => {
  const l = useLexicon();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);

  if (!subscription) return null;

  const isSelfServe = subscription.billing_track === 'self_serve';
  // Track B (agency seat) and the club track are both organization-managed:
  // billing is offline, the player talks to their admin / club.
  const isTeamAdmin = subscription.billing_track === 'team_admin' || subscription.billing_track === 'club';

  const onManageBilling = async () => {
    if (redirecting) return;
    setError(null);
    setRedirecting(true);
    try {
      const res = await createPortalSession();
      const url = res?.data?.url;
      if (!url) throw new Error('No portal URL returned');
      window.location.assign(url);
    } catch (err) {
      setError(err?.response?.data?.error || l(1723));
      setRedirecting(false);
    }
  };

  if (isTeamAdmin) {
    const orgName = subscription.organization?.display_name || subscription.organization?.name;
    return (
      <Section>
        <SectionTitle>{l(1711)}</SectionTitle>
        <Lede>
          {interp(l(1712), [<strong key="o">{orgName || l(1713)}</strong>])}
        </Lede>
        <Muted>
          {interp(l(1714), [
            <Link key="p" to="/subscription/upgrade">{l(1715)}</Link>,
          ])}
        </Muted>
      </Section>
    );
  }

  // Track A — self-serve.
  const status = subscription.status;
  const plan = planLabel(subscription);
  const renewsDate = subscription.current_period_end
    ? formatDateLong(subscription.current_period_end)
    : null;
  const cancelled = subscription.cancelled_at && status === 'cancelled';

  return (
    <Section>
      <SectionTitle>{l(1711)}</SectionTitle>

      {plan && (
        <Row>
          <Label>{l(1716)}</Label>
          <Value>{plan}</Value>
        </Row>
      )}
      {status === 'trial' && subscription.trial_ends_at && (
        <Row>
          <Label>{l(1717)}</Label>
          <Value>{new Date(subscription.trial_ends_at).toLocaleString()}</Value>
        </Row>
      )}
      {status === 'active' && renewsDate && (
        <Row>
          <Label>{l(1718)}</Label>
          <Value>{l(1719)} · {interp(l(1720), [renewsDate])}</Value>
        </Row>
      )}
      {cancelled && subscription.current_period_end && (
        <Row>
          <Label>{l(1721)}</Label>
          <Value>{formatDateLong(subscription.current_period_end)}</Value>
        </Row>
      )}
      {status === 'expired' && (
        <Row>
          <Label>{l(1718)}</Label>
          <Value>{l(1722)}</Value>
        </Row>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      {(status === 'trial' || status === 'active' || status === 'cancelled') && (
        <ManageButton type="button" onClick={onManageBilling} disabled={redirecting}>
          {redirecting ? l(1724) : l(1725)}
        </ManageButton>
      )}

      {status === 'expired' && (
        <ResubscribeLink to="/subscription/upgrade">{l(1710)}</ResubscribeLink>
      )}

      <Muted>{status === 'trial' ? l(1726) : l(1727)}</Muted>
    </Section>
  );
};

export default PlayerBillingSection;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${(p) => p.theme.cardBg ?? '#11141A'};
  border-radius: 12px;
  padding: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 4px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.blueish2};
`;

const Lede = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

const Muted = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${(p) => p.theme.blueish2};
  a { color: ${(p) => p.theme.green}; }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
`;

const Label = styled.span`
  color: ${(p) => p.theme.blueish2};
`;

const Value = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  text-align: right;
`;

const ManageButton = styled.button`
  margin-top: 8px;
  padding: 10px 14px;
  background: ${(p) => p.theme.green};
  color: ${(p) => p.theme.black};
  border: 0;
  border-radius: 8px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  cursor: pointer;
  &:disabled { opacity: 0.7; cursor: default; }
`;

const ResubscribeLink = styled(Link)`
  margin-top: 8px;
  padding: 10px 14px;
  background: ${(p) => p.theme.green};
  color: ${(p) => p.theme.black};
  border-radius: 8px;
  text-decoration: none;
  text-align: center;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
`;

const ErrorBox = styled.div`
  background: rgba(242, 10, 70, 0.12);
  border: 1px solid #F20A46;
  color: #F20A46;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
`;
